import type { ValueGetterParams } from 'ag-grid-community';
import type { ForwardDealInfo, VanillaOptionDealInfo } from '../../../../types/OTC/otcDealServerTypes';
import { isOTCForwardSecurity, isOTCOptionSecurity } from '../../../../types/OTC/otcSecurityTypeGuards';
import type { Security } from '../../../../types/Security';
import { SubProductTypeEnum } from '../../../../types/types';
import type { BlotterTableContextProps } from '../../BlotterTableContext';
import {
  getStrikeFwdPriceValueFromForwardDealInfo,
  getStrikeFwdPriceValueFromVanillaOptionDealInfo,
  type StrikeFwdPriceValue,
} from './strikeFwdPriceValue';

/** Minimal row shape for {@link strikeFwdPrice} on OTC derivatives deal blotters. */
export type StrikeFwdPriceRow =
  | { Symbol: string; SubProductType: typeof SubProductTypeEnum.OTCForward; DealInfo: ForwardDealInfo }
  | { Symbol: string; SubProductType: typeof SubProductTypeEnum.OTCVanillaOption; DealInfo: VanillaOptionDealInfo };

function getBlotterContextProps(context: unknown): BlotterTableContextProps | undefined {
  if (context && typeof context === 'object' && 'current' in context) {
    return (context as { current?: BlotterTableContextProps }).current;
  }
  return undefined;
}

/** Value for {@link strikeFwdPrice} on deal blotter rows (forward price or option strike/delta). */
export function getStrikeFwdPriceColumnValue(
  params: ValueGetterParams<StrikeFwdPriceRow>
): StrikeFwdPriceValue | undefined {
  const data = params.data;
  if (!data) {
    return undefined;
  }

  const blotterCtx = getBlotterContextProps(params.context);
  const security: Security | undefined = blotterCtx?.securitiesBySymbol?.get(data.Symbol);

  switch (data.SubProductType) {
    case SubProductTypeEnum.OTCForward:
      return getStrikeFwdPriceValueFromForwardDealInfo(
        data.DealInfo,
        isOTCForwardSecurity(security) ? security.ProductDetails.QuoteAsset : undefined
      );
    case SubProductTypeEnum.OTCVanillaOption: {
      if (isOTCOptionSecurity(security)) {
        const pd = security.ProductDetails;
        return getStrikeFwdPriceValueFromVanillaOptionDealInfo(data.DealInfo, pd.StrikePriceCcy);
      }

      return getStrikeFwdPriceValueFromVanillaOptionDealInfo(data.DealInfo);
    }
    default:
      return undefined;
  }
}
